// `TerminalRow` (beekeeper-agent-chat, `client-beekeeper-chat` "Every terminal reason renders as a
// legible transcript row stating what survived"; sketch `prototype/sketches/beekeeper-agent-chat/
// terminal-reasons.html`). A plain transcript row, never a modal dialog — every reason renders
// through this ONE shared treatment, including a reason this client does not specifically style
// (the follow-on `beekeeper-agent-chat-rounds`'s `phase_ended`, or anything else the wire ever
// sends): the copy table below is a lookup with a generic fallback, not an exhaustive switch that
// could reject an unrecognised value.

import type { CSSProperties } from 'react';

export interface TerminalRowProps {
  reason: string;
  version: number | null;
}

type Tone = 'good' | 'neutral' | 'warn' | 'bad';

interface ReasonCopy {
  label: string;
  body: string;
  tone: Tone;
}

/** The six terminal reasons this change defines (CONTRACT.md §6). Every one keeps the edits
 * already made and cuts the version if it changed the document — the copy says so rather than
 * leaving the player to guess what survived. */
const KNOWN_REASONS: Record<string, ReasonCopy> = {
  completed: {
    label: 'Completed',
    body: 'The Beekeeper finished and had nothing more to change.',
    tone: 'good',
  },
  stopped: {
    label: 'Stopped',
    body: 'You interrupted this turn. The edits it had already made are kept.',
    tone: 'neutral',
  },
  turnCap: {
    label: 'Turn cap',
    body: 'The Beekeeper used its steps for this turn and stopped partway. Ask again to continue.',
    tone: 'warn',
  },
  timeout: {
    label: 'Timeout',
    body: 'This turn ran past its time limit and was cut short.',
    tone: 'warn',
  },
  budgetExhausted: {
    label: 'Budget exhausted',
    body: 'This sandbox has used its Beekeeper allowance. Hand-editing still works.',
    tone: 'bad',
  },
  providerError: {
    // Player language, not the wire's. "Provider" is a fact about the server's LLM plumbing; the
    // player's fact is that the Beekeeper did not answer. The `reason` union is untouched — only
    // the words are.
    label: "Couldn't reach the Beekeeper",
    body: 'Nothing was charged; try again.',
    tone: 'bad',
  },
};

/** Turns an unrecognised camelCase/snake_case reason into a readable label (`phase_ended` ->
 * "Phase ended") — never blank, and never thrown on. */
function humanizeReason(reason: string): string {
  const spaced = reason
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  return spaced.length === 0 ? 'Ended' : spaced[0]!.toUpperCase() + spaced.slice(1);
}

function copyFor(reason: string): ReasonCopy {
  const known = KNOWN_REASONS[reason];
  if (known !== undefined) {
    return known;
  }
  // The shared-treatment fallback (client-beekeeper-chat: "MUST render through the shared
  // treatment rather than requiring a new one"): a legible, generic row rather than a blank or a
  // thrown error.
  return { label: humanizeReason(reason), body: 'This turn ended.', tone: 'neutral' };
}

const TONE_STYLE: Record<Tone, CSSProperties> = {
  good: { background: 'var(--success-soft)', borderColor: 'var(--success)', color: '#3D5B22' },
  neutral: { background: 'var(--surface-sunken)', borderColor: 'var(--line)', color: 'var(--ink-2)' },
  warn: { background: 'var(--honey-100)', borderColor: 'var(--honey-300)', color: 'var(--honey-700)' },
  bad: { background: 'var(--danger-soft)', borderColor: 'var(--danger)', color: '#7E2F1D' },
};

const GLYPH: Record<Tone, string> = { good: '✓', neutral: '■', warn: '⚑', bad: '⛔' };

export function TerminalRow({ reason, version }: TerminalRowProps) {
  const copy = copyFor(reason);
  const tone = TONE_STYLE[copy.tone];
  return (
    <div>
      <div
        data-testid="terminal-row"
        data-reason={reason}
        role="status"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--sp-2)',
          borderRadius: 'var(--r-sm)',
          padding: '9px 11px',
          fontFamily: 'var(--font-body)',
          fontSize: 13,
          lineHeight: 1.5,
          border: '1px solid var(--line)',
          ...tone,
        }}
      >
        <span aria-hidden="true" style={{ flex: 'none', width: 15, textAlign: 'center', fontSize: 12 }}>
          {GLYPH[copy.tone]}
        </span>
        <div>
          <b style={{ fontFamily: 'var(--font-mono)', fontWeight: 500, fontSize: 12 }}>{copy.label}</b>
          {' — '}
          {copy.body}
        </div>
      </div>
      {version !== null && (
        <div
          data-testid="terminal-row-version"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 6,
            marginLeft: 21,
            fontFamily: 'var(--font-display)',
            fontWeight: 500,
            fontSize: 12,
            color: 'var(--accent-strong)',
            background: 'var(--honey-100)',
            border: '1px solid var(--honey-300)',
            borderRadius: 'var(--r-pill)',
            padding: '4px 11px',
          }}
        >
          ✎ Set updated → v{version}
        </div>
      )}
    </div>
  );
}
